// features/admin/RechargeHistory.jsx
import React, { useState } from 'react';
import { useGetRechargeHistoryQuery } from '../../features/admin/adminApi';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  Box,
  Pagination,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  Card,
  CardHeader,
  CardContent,
  InputAdornment,
  Skeleton,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterAlt as FilterIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';

const RechargeHistory = () => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useState({
    search: '',
    name: '',
    status: '',
    page: 1,
    limit: 10
  });

  const { data, isLoading, isError, error, refetch } = useGetRechargeHistoryQuery(searchParams);

  const handlePageChange = (event, newPage) => {
    setSearchParams(prev => ({ ...prev, page: newPage }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({ ...prev, [name]: value, page: 1 }));
  };

  const handleRefresh = () => {
    refetch();
  };

  const getStatusChip = (status) => {
    const colorMap = {
      'Success': 'success',
      'Failure': 'error',
      'Pending': 'warning'
    };
    return (
      <Chip
        label={status}
        color={colorMap[status] || 'default'}
        variant="outlined"
        size="small"
        sx={{ 
          fontWeight: 600,
          borderRadius: 1,
          textTransform: 'capitalize'
        }}
      />
    );
  };

  return (
    <Card elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <CardHeader
        title={
          <Typography variant="h5" fontWeight={600}>
            Recharge Transactions
          </Typography>
        }
        subheader="View and manage all mobile recharge transactions"
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          py: 2,
          '& .MuiCardHeader-subheader': {
            color: theme.palette.primary.contrastText,
            opacity: 0.8
          }
        }}
      />
      
      <CardContent>
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mb: 3, 
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <TextField
            placeholder="Search by TXID or number..."
            variant="outlined"
            name="search"
            value={searchParams.search}
            onChange={handleFilterChange}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              sx: {
                backgroundColor: 'background.paper',
                borderRadius: 2,
                width: 280
              }
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: 210 }}>
            <InputLabel>
              <Box display="flex" alignItems="center" gap={1}>
                <FilterIcon fontSize="small" />
                <span>Transaction Status</span>
              </Box>
            </InputLabel>
            <Select
              name="status"
              value={searchParams.status}
              onChange={handleFilterChange}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <FilterIcon fontSize="small" />
                  <span>Transaction Status</span>
                </Box>
              }
              sx={{
                backgroundColor: 'background.paper',
                borderRadius: 2
              }}
            >
              <MenuItem value="">All Transactions</MenuItem>
              <MenuItem value="Success">Successful</MenuItem>
              <MenuItem value="Failure">Failed</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {isError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error.message || 'Failed to load data. Please try again.'}
          </Alert>
        )}

        <TableContainer 
          component={Paper} 
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ 
                backgroundColor: theme.palette.grey[50],
                '& th': {
                  fontWeight: 600,
                  color: theme.palette.text.secondary
                }
              }}>
                <TableCell>User</TableCell>
                <TableCell>Details</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton variant="text" /></TableCell>
                    <TableCell><Skeleton variant="text" /></TableCell>
                    <TableCell><Skeleton variant="text" /></TableCell>
                    <TableCell><Skeleton variant="text" /></TableCell>
                    <TableCell><Skeleton variant="text" /></TableCell>
                    <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
                  </TableRow>
                ))
              ) : data?.data?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No transactions found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data?.data?.map((record) => (
                  <TableRow 
                    key={record.id}
                    hover
                    sx={{
                      '&:last-child td': { borderBottom: 0 },
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover
                      }
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ 
                          bgcolor: theme.palette.primary.main,
                          width: 36, 
                          height: 36,
                          fontSize: 14
                        }}>
                          {record.user?.name?.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography fontWeight={500}>
                            {record.user?.name || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {record.user?.phone || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={500}>
                        {record.number}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {record.operatorCode} • TXID: {record.txid}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={600}>
                        ₹{record.amount.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Commission: ₹{record.userCommission}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(record.status)}
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {new Date(record.createdAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {new Date(record.createdAt).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {data?.totalPages > 1 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mt: 3,
            px: 1
          }}>
            <Typography variant="body2" color="textSecondary">
              Showing {(searchParams.page - 1) * searchParams.limit + 1} to{' '}
              {Math.min(searchParams.page * searchParams.limit, data.totalRecords)} of{' '}
              {data.totalRecords} entries
            </Typography>
            <Pagination
              count={data.totalPages}
              page={searchParams.page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 1
                }
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RechargeHistory;